import { useState, useEffect, useRef, JSX } from "react";
import {
  Gift,
  Send,
  TrendingUp,
  Package,
  ChevronRight,
  Search,
  Filter,
  Eye,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  ArrowDownRight,
  Sparkles,
  ShoppingBag,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import BaseApi from "../../services/base-api";
import LoadingComponent from "../../components/LoadingComponent";
import { useDebounce } from "../../hooks/useDebounce";
import Price from "../../components/Currency/Price";
import { Money } from "@mui/icons-material";
import Pagination from "../../components/Pagination";
import { LocalizedText } from "../../types/LocalizedText";

export interface GiftType {
  id: string;
  orderNumber: string;
  userId: string;
  deliveryId: string | null;

  status:
    | "confirmed"
    | "pending"
    | "processing"
    | "shipped"
    | "delivered"
    | "cancelled"
    | "refunded";
  paymentStatus: "paid" | "unpaid" | "failed";
  paymentMethod: "online" | "cod";

  paymentDetails: PaymentDetails;

  subtotal: string;
  tax: string;
  taxRate: string;
  discount: string;
  discountCode: string | null;
  shippingFee: string;
  total: number;
  currency: string;

  shippingAddress: Address;
  billingAddress: Address;

  customerNotes: string | null;
  internalNotes: string | null;

  trackingNumber: string | null;
  shippingCarrier: string | null;
  estimatedDeliveryDate: string | null;
  deliveredAt: string | null;

  isGift: boolean;
  giftMessage: string | null;
  recipientInfo: RecipientInfo;

  deliveryOTP: string;
  deliveryOTPVerified: boolean;

  assignedDriverId: string | null;

  source: "web" | "mobile";
  ipAddress: string;
  userAgent: string;

  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;

  customer: Customer;
  items: OrderItem[];
}

/* -------------------- Supporting Interfaces -------------------- */

export interface PaymentDetails {
  paidAt: string;
  provider: string;
  invoiceId: number;
  initiatedAt: string;
  invoiceStatus: string;
  transactions: Transaction[];
}

export interface Transaction {
  ECI: string;
  Country: string;
  TrackId: string;
  Currency: string;
  DueValue: string;
  IpAddress: string;
  PaymentId: string;
  VatAmount: string;
  CardNumber: string;
  ReferenceId: string;
  PaidCurrency: string;
  TransactionId: string;
  PaymentGateway: string;
  AuthorizationId: string;
  TransactionDate: string;
  TransationValue: string;
  PaidCurrencyValue: string;
  TransactionStatus: string;
  TotalServiceCharge: string;
  CustomerServiceCharge: string;
  Error: string | null;
  ErrorCode: string;
  Card: CardDetails;
}

export interface CardDetails {
  Brand: string;
  Token: string;
  Issuer: string;
  Number: string;
  PanHash: string;
  ExpiryYear: string;
  ExpiryMonth: string;
  NameOnCard: string;
  FundingMethod: string;
  IssuerCountry: string;
}

export interface Address {
  name: string;
  email: string | null;
  phone: string;
  street: string;
  apartment: string | null;
  building: string | null;
  floor: string | null;
  landmark: string | null;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  coordinates: {
    lat: number;
    lng: number;
  } | null;
  instructions: string | null;
}

export interface RecipientInfo {
  recipientName: string;
  recipientEmail: string;
  recipientPhone: string;
}

export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface OrderItem {
  id: string;
  orderId: string;
  productId: string;
  variationId: string | null;

  productName: LocalizedText;
  productSku: string;
  variationDetails: any;
  images: any;
  quantity: number;
  unitPrice: string;
  subtotal: string;
  discount: string;
  total: string;

  productSnapshot: ProductSnapshot;

  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;

  product?: Product;
}

export interface ProductSnapshot {
  sku: string;
  name: LocalizedText;
  price: number;
  images: any[];
  variation: any;
}

export interface Product {
  id: string;
  sku: string;
  name: LocalizedText;
  images: any[];
}

export interface GiftStatistics {
  total: number;
  totalValue: number;
  avgValue: number;
  today: number;
  byStatus: Record<string, number>;
}

export interface PopularProduct {
  id: string | number;
  productId?: string | number;
  productName: string;
  orderCount: number;
  totalRevenue: number;
  averageValue?: number;
  lastOrderDate?: string;
  category?: string;
  imageUrl?: string;
}

export interface GiftSuggestion {
  id: string | number;
  name?: string;
  productName?: string;
  description?: string;
  price?: number;
  recommendedAmount?: number;
  category?: string;
  popularityScore?: number;
  imageUrl?: string;
  tags?: string[];
  occasionType?: string;
}

export interface SendGiftRequest {
  recipientName: string;
  recipientEmail?: string;
  recipientPhone?: string;
  productId?: string | number;
  productName: string;
  amount: number;
  message?: string;
  deliveryAddress?: string;
  deliveryDate?: string;
}

export interface SendGiftResponse {
  success: boolean;
  message?: string;
  data?: GiftType;
  giftId?: string | number;
}

export interface TrackGiftResponse {
  success: boolean;
  data?: GiftType;
  trackingInfo?: {
    currentStatus: string;
    lastUpdate: string;
    estimatedDelivery?: string;
    trackingHistory?: TrackingEvent[];
  };
}

export interface TrackingEvent {
  status: string;
  timestamp: string;
  location?: string;
  description?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
}

// Filter and Search types
export type GiftStatus =
  | "all"
  | "confirmed"
  | "pending"
  | "processing"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "refunded";

export interface GiftFilters {
  status: GiftStatus;
  searchQuery: string;
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
}

export interface GiftSortOptions {
  field: "date" | "amount" | "status" | "recipient";
  direction: "asc" | "desc";
}

// Component Props types
export interface GiftCardProps {
  gift: GiftType;
  onViewDetails?: (gift: GiftType) => void;
  onTrack?: (giftId: string | number) => void;
}

export interface GiftModalProps {
  gift: GiftType | null;
  isOpen: boolean;
  onClose: () => void;
}

export interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const GiftManagement = () => {
  const { success, err } = useToast();
  const { t } = useTranslation();

  const [gifts, setGifts] = useState<GiftType[]>([]);
  const [filtered, setFiltered] = useState<GiftType[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const [statusFilter, setStatusFilter] = useState<GiftStatus>("all");
  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<"createdAt" | "name">("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [activeTab, setActiveTab] = useState<string>("overview");
  const [allGifts, setAllGifts] = useState<GiftType[]>([]);
  const [statistics, setStatistics] = useState<GiftStatistics | null>(null);
  const [popularProducts, setPopularProducts] = useState<PopularProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const latestRequestRef = useRef(0);
  const [selectedGift, setSelectedGift] = useState<GiftType | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedProducts, setExpandedProducts] = useState<
    Record<string, boolean>
  >({});
  const giftStatusOptions: Array<Exclude<GiftStatus, "all">> = [
    "pending",
    "confirmed",
    "processing",
    "shipped",
    "delivered",
    "cancelled",
    "refunded",
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    fetchGifts();
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    statusFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  // Gifts are fetched by the filter effect; these don't depend on search/filters
  const loadInitialData = async () => {
    try {
      await Promise.all([fetchStatistics(), fetchPopularProducts()]);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchGifts = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      const res = await BaseApi.get(`/gifts?${params.toString()}`);
      if (requestId !== latestRequestRef.current) return;
      if (res.success) {
        setGifts(res.data || []);
        setAllGifts(res.data || []);
        setFiltered(res.data || []);
        setApiTotalItems(res.pagination?.totalItems || res.data.length);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching gifts:", error);
      err(t("Failed to load gifts"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const fetchStatistics = async (): Promise<void> => {
    try {
      const res = await BaseApi.get("/gifts/stats/overview");
      if (res?.success && res?.data) {
        const byStatus = Array.isArray(res.data.byStatus)
          ? res.data.byStatus.reduce(
              (
                acc: Record<string, number>,
                item: { status?: string; count?: string | number },
              ) => {
                const status = String(item.status || "").toLowerCase();
                if (status) {
                  acc[status] = Number(item.count) || 0;
                }
                return acc;
              },
              {},
            )
          : {};

        setStatistics({
          total: Number(res.data.total) || 0,
          totalValue: Number(res.data.totalValue) || 0,
          avgValue: Number(res.data.avgValue) || 0,
          today: Number(res.data.today) || 0,
          byStatus: {
            pending: Number(byStatus.pending) || 0,
            confirmed: Number(byStatus.confirmed) || 0,
            processing: Number(byStatus.processing) || 0,
            shipped: Number(byStatus.shipped) || 0,
            delivered: Number(byStatus.delivered) || 0,
            cancelled: Number(byStatus.cancelled) || 0,
            refunded: Number(byStatus.refunded) || 0,
          },
        });
      } else {
        setStatistics({
          total: 0,
          totalValue: 0,
          avgValue: 0,
          today: 0,
          byStatus: {},
        });
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setStatistics({
        total: 0,
        totalValue: 0,
        avgValue: 0,
        today: 0,
        byStatus: {},
      });
    }
  };

  const fetchPopularProducts = async (): Promise<void> => {
    try {
      const res = await BaseApi.get("/gifts/popular-products");
      if (res?.success && res?.data) {
        setPopularProducts(res.data);
      } else {
        setPopularProducts([]);
      }
    } catch (error) {
      console.error("Error fetching popular products:", error);
      setPopularProducts([]);
    }
  };

  const updateGiftStatus = async (
    giftId: string | number,
    newStatus: string,
  ) => {
    try {
      const res = await BaseApi.patch(`/gifts/${giftId}/status`, {
        status: newStatus,
      });
      if (res.success) {
        success(t("Gift status updated successfully"));
        await fetchGifts();
      } else {
        err(t("Failed to update gift status"));
      }
    } catch (error) {
      console.error(t("An error occurred while updating gift status"), error);
    }
  };

  const viewGiftDetails = async (giftId: string | number): Promise<void> => {
    try {
      const res = await BaseApi.get(`/gifts/${giftId}`);
      if (res?.success && res?.data) {
        setSelectedGift(res.data);
      } else {
        err(t("Failed to load gift details"));
      }
    } catch (error) {
      console.error("Error fetching gift details:", error);
    }
  };

  const getStatusColor = (status?: string): string => {
    const statusMap: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      confirmed: "bg-sky-100 text-sky-800 border-sky-300",
      processing: "bg-blue-100 text-blue-800 border-[#0d223f]",
      shipped: "bg-purple-100 text-purple-800 border-purple-300",
      delivered: "bg-green-100 text-green-800 border-green-300",
      cancelled: "bg-red-100 text-red-800 border-red-300",
      refunded: "bg-orange-100 text-orange-800 border-orange-300",
    };
    return (
      statusMap[status?.toLowerCase() || ""] ||
      "bg-gray-100 text-gray-800 border-gray-300"
    );
  };

  const getStatusIcon = (status?: string): JSX.Element => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      pending: Clock,
      confirmed: CheckCircle,
      processing: Package,
      shipped: Truck,
      delivered: CheckCircle,
      cancelled: XCircle,
      refunded: ArrowDownRight,
    };
    const Icon = iconMap[status?.toLowerCase() || ""] || Clock;
    return <Icon className="w-4 h-4" />;
  };

  const toggleProductsVisibility = (giftId: string) => {
    setExpandedProducts((prev) => ({
      ...prev,
      [giftId]: !prev[giftId],
    }));
  };

  const formatGiftProductName = (name?: string): string => {
    const safeName = String(name || "").trim();
    if (!safeName) {
      return t("Product");
    }

    const match = safeName.match(/^(.*)\((.*)\)\s*$/);
    if (!match) {
      return safeName;
    }

    const baseName = match[1].trim();
    const attributes = match[2]
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const [key, ...rest] = part.split(":");
        if (!rest.length) {
          return part;
        }

        const value = rest.join(":").trim();
        if (
          !value ||
          value.toLowerCase() === "null" ||
          value.toLowerCase() === "undefined"
        ) {
          return "";
        }

        return `${key.trim()}: ${value}`;
      })
      .filter(Boolean);

    return attributes.length
      ? `${baseName} (${attributes.join(", ")})`
      : baseName;
  };

  const filteredGifts: GiftType[] = allGifts.filter((gift) => {
    const matchesSearch =
      debouncedSearch === "" ||
      gift.recipientInfo.recipientName
        ?.toLowerCase()
        .includes(debouncedSearch.toLowerCase()) ||
      `${gift.customer.firstName} ${gift.customer.lastName}`
        .toLowerCase()
        .includes(debouncedSearch.toLowerCase()) ||
      gift.items
        .map((item) =>
          formatGiftProductName(item.productName.en || item.productName.ar),
        )
        .join(" ")
        .toLowerCase()
        .includes(debouncedSearch.toLowerCase()) ||
      gift.id.toString().includes(debouncedSearch);

    const matchesFilter =
      statusFilter === "all" ||
      gift?.status?.toLowerCase() === statusFilter?.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  if (initialLoading) {
    return <LoadingComponent title={t("Gifts")} />;
  }

  // Pagination calculations
  // ========== UPDATE PAGINATION ==========
  const finalTotalItems = apiTotalItems;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);

  return (
    <div className="min-h-screen bg-[#f2e6db] p-2 sm:p-4 lg:p-4">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Serif+Display&display=swap');
        
        @keyframes slideInFromTop {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInFromBottom {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInFromLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        .animate-slide-top { animation: slideInFromTop 0.6s ease-out; }
        .animate-slide-bottom { animation: slideInFromBottom 0.6s ease-out; }
        .animate-slide-left { animation: slideInFromLeft 0.6s ease-out; }
        .animate-float { animation: float 3s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-4 mx-4 mt-2">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
              <Gift className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-black">
                {t("Gift Management")}
              </h1>
              <p className="text-gray-800 text-sm mt-1">
                {t("Manage all gift orders and track deliveries")}
              </p>
            </div>
          </div>

          {/* <Tooltip title={canAddEdit ? t("Add Gifts") : t("You don't have permission to add gifts")} placement="top">
            <button
              disabled={!canAddEdit}
              onClick={openNewOrderModal}
              className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg font-semibold"
            >
              <Plus className="w-5 h-5" />
              {t("New Order")}
            </button>
          </Tooltip> */}
        </div>
      </div>

      <div className="mx-auto p-4 space-y-6">
        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-2 animate-slide-bottom">
            {giftStatusOptions.map((status) => {
              const statusColors: Record<string, string> = {
                pending: "border-yellow-200 bg-yellow-50 text-yellow-700",
                confirmed: "border-sky-200 bg-sky-50 text-sky-700",
                processing: "border-blue-200 bg-blue-50 text-blue-700",
                shipped: "border-purple-200 bg-purple-50 text-purple-700",
                delivered: "border-green-200 bg-green-50 text-green-700",
                cancelled: "border-red-200 bg-red-50 text-red-700",
                refunded: "border-orange-200 bg-orange-50 text-orange-700",
              };
              const statusIcons: Record<string, React.ComponentType<any>> = {
                pending: Clock,
                confirmed: CheckCircle,
                processing: Package,
                shipped: Truck,
                delivered: CheckCircle,
                cancelled: XCircle,
                refunded: ArrowDownRight,
              };
              const StatusIcon = statusIcons[status] || Gift;

              return (
                <div
                  key={status}
                  className={`rounded-2xl p-4 shadow-lg border-2 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 ${statusColors[status]}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="rounded-xl p-3 bg-white/80">
                      <StatusIcon className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-3">
                    <div className="text-sm font-medium">
                      {t(
                        status
                          .split("_")
                          .map(
                            (part) =>
                              part.charAt(0).toUpperCase() + part.slice(1),
                          )
                          .join(" "),
                      )}
                    </div>
                    <div className="text-3xl font-bold text-gray-900">
                      {statistics.byStatus?.[status] || 0}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-2 animate-slide-left border-2 border-gray-200">
          <div className="flex gap-2 overflow-x-auto">
            {[
              { id: "overview", label: "All Gifts", icon: Gift },
              { id: "popular", label: "Popular Products", icon: TrendingUp },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-gradient-to-r from-gray-700 to-black text-white shadow-lg"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline">{t(tab.label)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* All Gifts Tab */}
        {activeTab === "overview" && (
          <div className="space-y-4 animate-slide-bottom">
            {/* Search and Filter */}
            <div className="bg-white rounded-2xl shadow-lg p-4 border-2 border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t(
                      "Search by recipient, sender, product, or ID...",
                    )}
                    className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 focus:border-rose-500 focus:outline-none"
                  />
                </div>

                <div className="relative">
                  <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as GiftStatus)}
                    className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-300 focus:border-rose-500 focus:outline-none appearance-none cursor-pointer"
                  >
                    <option value="all">{t("All Status")}</option>
                    {giftStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {t(
                          status
                            .split("_")
                            .map(
                              (part) =>
                                part.charAt(0).toUpperCase() + part.slice(1),
                            )
                            .join(" "),
                        )}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Gifts List */}
            <div className="space-y-3 sm:space-y-4">
              {loading ? (
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-8 sm:p-12 text-center border-2 border-gray-200">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-rose-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm sm:text-base">
                    {t("Loading gifts...")}
                  </p>
                </div>
              ) : filteredGifts.length === 0 ? (
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-8 sm:p-12 text-center border-2 border-gray-200">
                  <Gift className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-base sm:text-lg mb-2">
                    {t("No gifts found")}
                  </p>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    {debouncedSearch || statusFilter !== "all"
                      ? t("Try adjusting your filters")
                      : t("No gift orders yet")}
                  </p>
                </div>
              ) : (
                filteredGifts.map((gift, index) => (
                  <div
                    key={gift?.id || index}
                    className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-200 hover:shadow-2xl transition-all duration-300 overflow-hidden"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-3 sm:px-6 py-3 sm:py-4 border-b-2 border-gray-200">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        {/* Left section */}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                          {/* Icon + Order Info */}
                          <div className="flex items-center gap-3">
                            <div className="bg-white rounded-lg p-2 shadow-sm shrink-0">
                              <Gift className="w-5 h-5 text-gray-700" />
                            </div>

                            <div>
                              <p className="text-sm font-semibold text-blue-700">
                                #{gift?.orderNumber || gift?.id || "N/A"}
                              </p>
                              <p className="text-xs text-gray-400">
                                {gift?.createdAt
                                  ? new Date(gift.createdAt).toLocaleDateString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        year: "2-digit",
                                      },
                                    )
                                  : t("Date N/A")}
                              </p>
                            </div>
                          </div>

                          {/* Status Badges */}
                          <div className="flex flex-wrap gap-2">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border-2 ${getStatusColor(
                                gift?.status,
                              )}`}
                            >
                              {getStatusIcon(gift?.status)}
                              <span>{gift?.status}</span>
                            </span>

                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border-2 ${
                                gift?.paymentStatus === "paid"
                                  ? "bg-green-100 text-green-700 border-green-300"
                                  : gift?.paymentStatus === "failed"
                                    ? "bg-red-100 text-red-700 border-red-300"
                                    : "bg-yellow-100 text-yellow-700 border-yellow-300"
                              }`}
                            >
                              <Money className="w-4 h-4" />
                              <span>{gift?.paymentStatus}</span>
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          <select
                            value={gift.status}
                            onChange={(e) =>
                              updateGiftStatus(gift.id, e.target.value)
                            }
                            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold focus:border-gray-400 focus:outline-none"
                          >
                            {giftStatusOptions.map((status) => (
                              <option key={status} value={status}>
                                {t(
                                  status
                                    .split("_")
                                    .map(
                                      (part) =>
                                        part.charAt(0).toUpperCase() +
                                        part.slice(1),
                                    )
                                    .join(" "),
                                )}
                              </option>
                            ))}
                          </select>

                          <button
                            onClick={() => viewGiftDetails(gift?.id)}
                            className="flex items-center justify-center gap-2 bg-gradient-to-r from-gray-700 to-black text-white px-4 py-2.5 rounded-lg hover:shadow-lg transition-all font-semibold text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            <span>{t("View Details")}</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Main Content */}
                    <div className="p-3 sm:p-4">
                      <div className="space-y-3 sm:space-y-4">
                        {/* Gift Message */}
                        {gift?.giftMessage && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-rose-600" />
                              <p className="text-xs sm:text-sm font-semibold text-gray-700">
                                {t("Gift Message")}
                              </p>
                            </div>
                            <div className="bg-rose-50 border-l-4 border-rose-400 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
                              <p className="text-gray-900 italic text-xs sm:text-sm line-clamp-3">
                                "{gift.giftMessage}"
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Sender & Recipient Grid */}
                        <div className="grid grid-cols-2 xs:grid-cols-2 gap-2 sm:gap-3">
                          {/* Sender */}
                          <div>
                            <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                              <Send className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                              <p className="text-xs sm:text-sm font-semibold text-gray-700">
                                {t("From")}
                              </p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-2 sm:p-4">
                              <p className="font-bold text-slate-900 text-xs sm:text-sm mb-1">
                                {gift?.customer?.firstName ||
                                gift?.customer?.lastName
                                  ? `${gift.customer.firstName || ""} ${gift.customer.lastName || ""}`.trim()
                                  : "-"}
                              </p>
                              {gift?.customer?.email && (
                                <p className="text-xs text-blue-700 flex items-center gap-1 truncate mt-1">
                                  <span>📧</span>
                                  <span className="truncate">
                                    {gift.customer.email}
                                  </span>
                                </p>
                              )}
                              {gift?.customer?.phone && (
                                <p className="text-xs text-blue-700 flex items-center gap-1 mt-0.5">
                                  <span>📱</span> {gift.customer.phone}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Recipient */}
                          <div>
                            <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                              <Send className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 rotate-90" />
                              <p className="text-xs sm:text-sm font-semibold text-gray-700">
                                {t("To")}
                              </p>
                            </div>
                            <div className="bg-green-50 rounded-lg sm:rounded-xl p-2 sm:p-4">
                              <p className="font-bold text-gray-900 text-xs sm:text-sm mb-1">
                                {gift?.recipientInfo?.recipientName || "-"}
                              </p>
                              {gift?.recipientInfo?.recipientEmail && (
                                <p className="text-xs text-gray-600 flex items-center gap-1 truncate mt-1">
                                  <span>📧</span>
                                  <span className="truncate">
                                    {gift.recipientInfo.recipientEmail}
                                  </span>
                                </p>
                              )}
                              {gift?.recipientInfo?.recipientPhone && (
                                <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                                  <span>📱</span>{" "}
                                  {gift.recipientInfo.recipientPhone}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Products Section */}
                        <div className="grid grid-cols-2 xs:grid-cols-2 gap-2 sm:gap-3">
                          <div>
                            <button
                              type="button"
                              onClick={() => toggleProductsVisibility(gift.id)}
                              className="w-full flex items-center justify-between gap-2 mb-2 text-left"
                            >
                              <div className="flex items-center gap-2">
                                <ChevronRight
                                  className={`w-3 h-3 sm:w-4 sm:h-4 text-blue-600 transition-transform ${
                                    expandedProducts[gift.id]
                                      ? "rotate-90"
                                      : ""
                                  }`}
                                />
                                <Package className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                                <p className="text-xs sm:text-sm font-semibold text-gray-700">
                                  {t("Products")}
                                </p>
                              </div>
                            </button>
                            {expandedProducts[gift.id] && (
                              <div className="bg-[#0d223f] rounded-lg sm:rounded-xl p-2.5 sm:p-3 border-l-4 border-[#0d223f]">
                                {gift?.items?.map((item, idx) => (
                                  <div
                                    key={idx}
                                    className={`${idx > 0 ? "mt-2 pt-2 border-t border-blue-900/60" : ""}`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <p className="text-xs sm:text-sm font-semibold text-white flex-1 line-clamp-2">
                                        {formatGiftProductName(
                                          item.productName.en ||
                                            item.productName.ar,
                                        )}
                                      </p>
                                      <p className="text-xs sm:text-sm font-semibold text-blue-200 whitespace-nowrap">
                                        <Price amount={item.total || 0} />
                                      </p>
                                    </div>
                                    <p className="text-xs text-blue-100 mt-0.5">
                                      {t("Qty")}: {item.quantity}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Delivery Address */}
                          {gift?.shippingAddress && (
                            <div>
                              <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                                <p className="text-xs sm:text-sm font-semibold text-gray-700">
                                  {t("Delivery Address")}
                                </p>
                              </div>
                              <div className="bg-purple-50 rounded-lg sm:rounded-xl p-2 sm:p-3">
                                <p className="font-semibold text-gray-900 text-xs sm:text-sm mb-1">
                                  {gift.shippingAddress.name || "-"}
                                </p>
                                <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
                                  {[
                                    gift.shippingAddress.street,
                                    gift.shippingAddress.building,
                                    gift.shippingAddress.landmark,
                                    gift.shippingAddress.city,
                                    gift.shippingAddress.state,
                                    gift.shippingAddress.zipCode,
                                    gift.shippingAddress.country,
                                  ]
                                    .filter(Boolean)
                                    .join(", ")}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Bottom Actions Section */}
                        <div className="pt-3 sm:pt-4 border-t-2 border-gray-200 space-y-2 sm:space-y-3">
                          {/* Action Buttons */}
                          {/* Total - Desktop Only */}
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              {t("Total Amount")}
                            </span>
                            <span className="text-xl sm:text-2xl font-bold text-gray-900">
                              <Price amount={gift?.total || 0} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Popular Products Tab */}
        {activeTab === "popular" && (
          <div className="space-y-6 animate-slide-bottom">
            <div className="bg-white rounded-2xl shadow-lg p-4 border-2 border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-yellow-500" />
                {t("Most Popular Gift Products")}
              </h2>

              {loading ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">
                    {t("Loading popular products...")}
                  </p>
                </div>
              ) : popularProducts.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">
                    {t("No popular products yet")}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4">
                  {popularProducts.map((product, index) => (
                    <div
                      key={product?.id || index}
                      className="bg-gradient-to-br from-gray-700 to-black rounded-xl sm:rounded-2xl p-4 sm:p-4 border-2 border-orange-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex items-start justify-between mb-3 sm:mb-4">
                        <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow">
                          <Package className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
                        </div>
                        <span className="bg-yellow-400 text-yellow-900 px-2.5 sm:px-3 py-1 rounded-full text-xs font-bold">
                          #{index + 1}
                        </span>
                      </div>

                      <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                        {product?.productName || t("Product")}
                      </h3>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs sm:text-sm text-gray-300">
                            {t("Orders")}
                          </span>
                          <span className="text-base sm:text-lg font-bold text-white">
                            {product?.orderCount || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs sm:text-sm text-gray-300">
                            {t("Revenue")}
                          </span>
                          <span className="text-base sm:text-lg font-bold text-green-400">
                            <Price amount={product?.totalRevenue || 0} />
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t-2 border-gray-600">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">
                            {t("Avg. Value")}
                          </span>
                          <span className="text-sm font-bold text-white">
                            <Price
                              amount={
                                product.orderCount
                                  ? Number(product.totalRevenue) /
                                    product.orderCount
                                  : 0
                              }
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Gift Details Modal */}
      {selectedGift && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedGift(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-gray-700 to-black text-white p-4 rounded-t-3xl">
              <h3 className="text-2xl font-bold">{t("Gift Details")}</h3>
              <p className="text-rose-100 text-sm">
                Order #{selectedGift?.id || "N/A"}
              </p>
            </div>

            <div className="p-4 space-y-6">
              {/* Status and Payment Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{t("Status")}</p>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold border-2 ${getStatusColor(
                      selectedGift?.status,
                    )}`}
                  >
                    {getStatusIcon(selectedGift?.status)}
                    {selectedGift?.status || "Unknown"}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">
                    {t("Payment Status")}
                  </p>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                      selectedGift?.paymentStatus === "paid"
                        ? "bg-green-100 text-green-700"
                        : selectedGift?.paymentStatus === "failed"
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {selectedGift?.paymentStatus || "-"}
                  </span>
                </div>
              </div>

              {/* Order Details */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {t("Order Number")}
                  </span>
                  <span className="font-bold text-gray-900">
                    #{selectedGift?.orderNumber || "-"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t("Subtotal")}</span>
                  <span className="font-semibold text-gray-900">
                    <Price amount={selectedGift?.subtotal || 0} />
                  </span>
                </div>
                {selectedGift?.discount &&
                  parseFloat(selectedGift.discount) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        {t("Discount")}
                      </span>
                      <span className="font-semibold text-green-600">
                        -<Price amount={selectedGift.discount} />
                      </span>
                    </div>
                  )}
                {selectedGift?.tax && parseFloat(selectedGift.tax) > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t("Tax")}</span>
                    <span className="font-semibold text-gray-900">
                      <Price amount={selectedGift.tax} />
                    </span>
                  </div>
                )}
                {selectedGift?.shippingFee &&
                  parseFloat(selectedGift.shippingFee) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        {t("Shipping Fee")}
                      </span>
                      <span className="font-semibold text-gray-900">
                        <Price amount={selectedGift.shippingFee} />
                      </span>
                    </div>
                  )}
                <div className="flex justify-between items-center pt-2 border-t-2 border-gray-200">
                  <span className="text-base font-bold text-gray-700">
                    {t("Total Amount")}
                  </span>
                  <span className="text-2xl font-bold text-gray-900">
                    <Price amount={selectedGift?.total || 0} />
                  </span>
                </div>
              </div>

              {/* Products */}
              <div className="space-y-4 border-t-2 border-gray-200 pt-6">
                <div>
                  <p className="text-sm text-gray-500 mb-2">{t("Products")}</p>
                  <div className="space-y-2">
                    {selectedGift?.items?.length > 0 ? (
                      selectedGift.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                        >
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {formatGiftProductName(
                                item.productName?.en || item.productName?.ar,
                              )}
                            </p>
                            {item.productSku && (
                              <p className="text-xs text-gray-500">
                                SKU: {item.productSku}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">
                              {item.quantity || 1}x{" "}
                              <Price amount={item.unitPrice || 0} />
                            </p>
                            <p className="text-xs text-gray-500">
                              <Price amount={item.total || 0} />
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">-</p>
                    )}
                  </div>
                </div>

                {/* Gift Message */}
                {selectedGift?.giftMessage && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      {t("Gift Message")}
                    </p>
                    <div className="bg-rose-50 border-l-4 border-rose-400 p-3 rounded">
                      <p className="text-gray-900 italic">
                        "{selectedGift.giftMessage}"
                      </p>
                    </div>
                  </div>
                )}

                {/* Sender and Recipient Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-700 mb-2">
                      {t("From (Sender)")}
                    </p>
                    <p className="font-bold text-slate-900">
                      {selectedGift?.customer?.firstName ||
                      selectedGift?.customer?.lastName
                        ? `${selectedGift.customer.firstName || ""} ${selectedGift.customer.lastName || ""}`.trim()
                        : "-"}
                    </p>
                    {selectedGift?.customer?.email && (
                      <p className="text-sm text-blue-700 mt-1">
                        📧 {selectedGift.customer.email}
                      </p>
                    )}
                    {selectedGift?.customer?.phone && (
                      <p className="text-sm text-blue-700">
                        📱 {selectedGift.customer.phone}
                      </p>
                    )}
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-700 mb-2">
                      {t("To (Recipient)")}
                    </p>
                    <p className="font-bold text-gray-900">
                      {selectedGift?.recipientInfo?.recipientName || "-"}
                    </p>
                    {selectedGift?.recipientInfo?.recipientEmail && (
                      <p className="text-sm text-gray-600 mt-1">
                        📧 {selectedGift.recipientInfo.recipientEmail}
                      </p>
                    )}
                    {selectedGift?.recipientInfo?.recipientPhone && (
                      <p className="text-sm text-gray-600">
                        📱 {selectedGift.recipientInfo.recipientPhone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Delivery Address */}
                {selectedGift?.shippingAddress && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">
                      {t("Delivery Address")}
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                      <p className="font-bold text-gray-900">
                        {selectedGift.shippingAddress.name || "-"}
                      </p>
                      <p className="text-gray-700">
                        {[
                          selectedGift.shippingAddress.street,
                          selectedGift.shippingAddress.apartment,
                          selectedGift.shippingAddress.building,
                          selectedGift.shippingAddress.floor,
                        ]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </p>
                      {selectedGift.shippingAddress.landmark && (
                        <p className="text-sm text-gray-600">
                          📍 {selectedGift.shippingAddress.landmark}
                        </p>
                      )}
                      <p className="text-gray-700">
                        {[
                          selectedGift.shippingAddress.city,
                          selectedGift.shippingAddress.state,
                          selectedGift.shippingAddress.zipCode,
                        ]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </p>
                      <p className="text-gray-700">
                        {selectedGift.shippingAddress.country || "-"}
                      </p>
                      {selectedGift.shippingAddress.phone && (
                        <p className="text-sm text-gray-600 mt-2">
                          📱 {selectedGift.shippingAddress.phone}
                        </p>
                      )}
                      {selectedGift.shippingAddress.instructions && (
                        <p className="text-sm text-gray-600 italic mt-2">
                          💬 {selectedGift.shippingAddress.instructions}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Tracking Information */}
                {(selectedGift?.trackingNumber ||
                  selectedGift?.shippingCarrier ||
                  selectedGift?.estimatedDeliveryDate) && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">
                      {t("Tracking Information")}
                    </p>
                    <div className="bg-[#0d223f] rounded-lg p-4 space-y-2">
                      {selectedGift.trackingNumber && (
                        <div className="flex justify-between">
                          <span className="text-sm text-blue-100">
                            {t("Tracking Number")}
                          </span>
                          <span className="font-semibold text-white">
                            {selectedGift.trackingNumber}
                          </span>
                        </div>
                      )}
                      {selectedGift.shippingCarrier && (
                        <div className="flex justify-between">
                          <span className="text-sm text-blue-100">
                            {t("Carrier")}
                          </span>
                          <span className="font-semibold text-white">
                            {selectedGift.shippingCarrier}
                          </span>
                        </div>
                      )}
                      {selectedGift.estimatedDeliveryDate && (
                        <div className="flex justify-between">
                          <span className="text-sm text-blue-100">
                            {t("Est. Delivery")}
                          </span>
                          <span className="font-semibold text-white">
                            {new Date(
                              selectedGift.estimatedDeliveryDate,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {selectedGift.deliveredAt && (
                        <div className="flex justify-between">
                          <span className="text-sm text-blue-100">
                            {t("Delivered At")}
                          </span>
                          <span className="font-semibold text-green-300">
                            {new Date(
                              selectedGift.deliveredAt,
                            ).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Payment Method */}
                {selectedGift?.paymentMethod && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">
                        {t("Payment Method")}
                      </p>
                      <p className="font-semibold text-gray-900 capitalize">
                        {selectedGift.paymentMethod === "cod"
                          ? t("Pay and Deliver")
                          : t("Online Payment")}
                      </p>
                    </div>
                    {selectedGift.discountCode && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">
                          {t("Discount Code")}
                        </p>
                        <p className="font-semibold text-green-700">
                          {selectedGift.discountCode}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                {(selectedGift?.customerNotes ||
                  selectedGift?.internalNotes) && (
                  <div className="space-y-3">
                    {selectedGift.customerNotes && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">
                          {t("Customer Notes")}
                        </p>
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                          <p className="text-gray-900">
                            {selectedGift.customerNotes}
                          </p>
                        </div>
                      </div>
                    )}
                    {selectedGift.internalNotes && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">
                          {t("Internal Notes")}
                        </p>
                        <div className="bg-purple-50 border-l-4 border-purple-400 p-3 rounded">
                          <p className="text-gray-900">
                            {selectedGift.internalNotes}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      {t("Order Date")}
                    </p>
                    <p className="text-gray-900">
                      {selectedGift?.createdAt
                        ? new Date(selectedGift.createdAt).toLocaleString()
                        : "-"}
                    </p>
                  </div>
                  {selectedGift?.updatedAt &&
                    selectedGift.updatedAt !== selectedGift.createdAt && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">
                          {t("Last Updated")}
                        </p>
                        <p className="text-gray-900">
                          {new Date(selectedGift.updatedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                </div>
              </div>

              <button
                onClick={() => setSelectedGift(null)}
                className="w-full bg-gradient-to-r from-gray-700 to-black text-white py-4 rounded-xl hover:shadow-lg transition-all duration-300 font-bold"
              >
                {t("Close")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Pagination indicators */}
      {finalTotalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={finalTotalPages}
          totalItems={finalTotalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          showPageSizeSelector={true}
          isLoading={loading}
          compact={true}
        />
      )}
    </div>
  );
};

export default GiftManagement;
